// Locale-aware number/date formatting for the Sales dashboard.

export function fmtNumber(v, lang, decimals = 2) {
    if (v === null || v === undefined || Number.isNaN(v)) return "\u2014";
    const loc = lang === "ar" ? "ar-SA" : "en-US";
    return new Intl.NumberFormat(loc, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(v);
}

export function fmtInt(v, lang) {
    return fmtNumber(v, lang, 0);
}

// Compact ticks for chart axes (kept Latin for readability in both languages).
export function fmtCompact(v) {
    if (v === null || v === undefined || Number.isNaN(v)) return "";
    return new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(v);
}

// Format a metric value by its unit. Returns a display string (or em dash).
export function fmtByUnit(value, unit, lang) {
    if (value === null || value === undefined) return "\u2014";
    if (unit === "number") return fmtInt(value, lang);
    if (unit === "percent") return `${fmtNumber(value, lang, 1)}%`;
    return fmtNumber(value, lang, 2); // currency
}

// A server ISO timestamp as Riyadh wall-clock date + time ("19 Oct 2026, 08:53"),
// whatever zone the device is in; Gregorian calendar and Latin digits in Arabic too.
export function fmtRiyadhDateTime(iso, lang) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", {
        timeZone: "Asia/Riyadh", calendar: "gregory", numberingSystem: "latn",
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
}

export function currencySuffix(lang) {
    return lang === "ar" ? "ر.س" : "SAR";
}

// "2026-03-15" -> short label for axis ("03-15"); full date for tooltip via toLocale.
export function shortDate(iso) {
    return iso ? iso.slice(5) : "";
}

export function localeDate(iso, lang) {
    if (!iso) return "";
    try {
        return new Date(iso + "T00:00:00").toLocaleDateString(
            lang === "ar" ? "ar-SA" : "en-US",
            { day: "numeric", month: "short", year: "numeric" }
        );
    } catch {
        return iso;
    }
}

export function isoFromDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export function dateFromIso(iso) {
    return iso ? new Date(iso + "T00:00:00") : undefined;
}

export function lastDayOfMonth(ym) {
    const y = Number(ym.slice(0, 4));
    const m = Number(ym.slice(5, 7));
    const d = new Date(y, m, 0).getDate();
    return `${ym}-${String(d).padStart(2, "0")}`;
}

// Recharts series palette mapped to brand chart tokens.
export const CHART_COLORS = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
];

// 8-slice palette (brand hue family) for the expense-by-group breakdown.
export const FIN_COLORS = [
    "hsl(28 62% 50%)",   // operating - orange
    "hsl(18 70% 30%)",   // salaries - brown
    "hsl(203 55% 60%)",  // app_commission - sky (key cost stands out)
    "hsl(40 22% 52%)",   // admin - tan
    "hsl(20 12% 24%)",   // labor_gov - ink
    "hsl(28 60% 68%)",   // other - light orange
    "hsl(203 42% 38%)",  // rent - deep sky
    "hsl(40 28% 72%)",   // depreciation - light beige
];

// Profit / loss semantic colors (from index.css tokens).
export const PROFIT_COLOR = "var(--success)";
export const LOSS_COLOR = "var(--danger)";
